export const ARTICLES = [
  {
    slug: "will-vs-trust-basics",
    title: "Will vs. Trust Basics",
    category: "Estate Basics",
    minutes: 4,
    excerpt: "Two common tools, one goal: making sure your wishes are clear. Here's how wills and trusts differ in plain English.",
    takeaways: [
      "A will explains who receives your assets and who should care for minor children.",
      "A trust can hold assets during your lifetime and pass them on without probate.",
      "Many families use both. Which tools fit your situation is a conversation for a qualified attorney.",
    ],
    sections: [
      {
        heading: "What is a will?",
        body: [
          "A will is a written document that explains what you want to happen after you pass away. It names who should receive your belongings, who should carry out your wishes (your executor), and who should care for any minor children.",
          "A will only takes effect after death, and assets controlled by a will may require a court process called probate before they are distributed.",
        ],
      },
      {
        heading: "What is a trust?",
        body: [
          "A trust is a legal arrangement that holds assets for the people you choose. A revocable living trust is created while you're alive, and you typically stay in control of everything in it.",
          "A living trust may help avoid probate, but only for assets that are properly transferred to and funded into the trust. Assets left outside the trust may still go through probate. Families often find a funded trust makes the transfer process simpler and more private.",
        ],
      },
      {
        heading: "Which one is right for you?",
        body: [
          "There's no universal answer — it depends on your family, your assets, and your goals. An estate-planning attorney can help you understand which approach fits your situation. Legacy Wave's role is to help you arrive at that conversation informed and confident.",
        ],
      },
    ],
  },
  {
    slug: "what-is-probate",
    title: "What Is Probate?",
    category: "Estate Basics",
    minutes: 4,
    excerpt: "Probate sounds intimidating, but it's simply the court process for settling an estate. Here's what families should know.",
    takeaways: [
      "Probate is the court-supervised process of validating a will and distributing assets.",
      "It can take months and involves court costs, and it becomes public record.",
      "Certain assets — like accounts with named beneficiaries — often pass outside probate.",
    ],
    sections: [
      {
        heading: "The simple definition",
        body: [
          "Probate is the court-supervised administration of an estate after someone passes away. It may include validating a will, appointing a personal representative, paying valid debts, and distributing probate assets to the right people. If there's no will, the court follows state rules instead.",
          "Not everything goes through probate — only assets that are part of the probate estate. Assets controlled by a will may require probate, while accounts with named beneficiaries often do not.",
        ],
      },
      {
        heading: "Why families plan around it",
        body: [
          "Probate takes time — often several months to over a year — and comes with court fees. Because it's a public process, the details of a probate estate can become public record.",
          "None of this makes probate 'bad.' It simply means many families prefer to make things easier for their loved ones where they can.",
        ],
      },
      {
        heading: "What often avoids probate",
        body: [
          "Life insurance with named beneficiaries, retirement accounts with beneficiaries, property titled with a valid right of survivorship, and assets properly transferred into a living trust typically pass directly to the people you chose — no court process required. Results depend on how each asset is titled and on state law. Keeping beneficiary designations current is one of the simplest planning steps you can take.",
        ],
      },
    ],
  },
  {
    slug: "what-happens-to-your-home",
    title: "What Happens to Your Home When You Die?",
    category: "Asset Transfer",
    minutes: 5,
    excerpt: "For most families, the home is the biggest asset — and the one with the most questions attached. A calm look at what can happen.",
    takeaways: [
      "What happens to your home depends on how the title is held and what your plan says.",
      "Without clear instructions, a home may go through probate before it can be transferred or sold.",
      "Reviewing your deed and your plan together helps avoid surprises.",
    ],
    sections: [
      {
        heading: "It starts with the title",
        body: [
          "How your home is owned matters. Property titled with a valid right of survivorship may pass outside probate — the result depends on the deed, the ownership structure, and state law. A home owned in one name usually follows the instructions in a will — or state law if there isn't one — and those assets may require probate.",
        ],
      },
      {
        heading: "Common paths a home can take",
        body: [
          "A surviving spouse's rights depend on the deed, the form of ownership, the estate plan, and applicable state law. Children may inherit the home and decide to keep or sell it. If there's a mortgage, the loan still needs to be handled, which is one reason families consider how payments would be covered.",
          "Without a plan, the court may need to appoint someone to make these decisions — which takes time during an already difficult season.",
        ],
      },
      {
        heading: "Questions worth asking",
        body: [
          "Whose name is on the deed? Does anyone know your wishes for the home? Would your family be able to cover the mortgage and upkeep during a transition? These are exactly the kinds of questions a Legacy Review conversation can help you organize.",
        ],
      },
    ],
  },
  {
    slug: "life-insurance-basics",
    title: "Life Insurance Basics",
    category: "Family Protection",
    minutes: 5,
    excerpt: "What life insurance is, the main types, and how families commonly use it — explained without jargon or pressure.",
    takeaways: [
      "Life insurance provides money to the people you choose when you pass away.",
      "Term insurance covers a set period; permanent insurance is designed to last a lifetime.",
      "Coverage needs often change after major life events — reviews matter.",
    ],
    sections: [
      {
        heading: "What it does",
        body: [
          "Life insurance pays a benefit to your chosen beneficiaries when you pass away. Death benefits are generally received federal-income-tax-free by beneficiaries, although exceptions may apply — a qualified tax professional can address individual circumstances.",
          "Families commonly use life insurance to replace income, pay off a mortgage, cover final expenses, or leave something meaningful for children and grandchildren.",
        ],
      },
      {
        heading: "The two main types",
        body: [
          "Term life insurance covers you for a set number of years — often 10, 20, or 30 — and is usually the most affordable option. Permanent life insurance, such as whole life, is designed to last your entire life and may build cash value over time.",
          "Each serves different goals, and many families use a combination over their lifetime.",
        ],
      },
      {
        heading: "Why reviews matter",
        body: [
          "Marriages, new grandchildren, a paid-off mortgage, retirement — life changes, and coverage that fit ten years ago may not fit today. A periodic review simply confirms your coverage still matches your family's needs. Nothing more, nothing less.",
        ],
      },
    ],
  },
  {
    slug: "understanding-beneficiaries",
    title: "Understanding Beneficiaries",
    category: "Family Protection",
    minutes: 4,
    excerpt: "A beneficiary designation is a small form with enormous power. Here's why reviewing yours belongs on every family's checklist.",
    takeaways: [
      "Beneficiary designations usually override what a will says.",
      "Life changes — marriage, divorce, new grandchildren — are reasons to review.",
      "A review takes minutes and costs nothing.",
    ],
    sections: [
      {
        heading: "What a beneficiary is",
        body: [
          "A beneficiary is the person (or people) you name to receive money from accounts like life insurance policies, retirement accounts, and certain bank accounts. When you pass away, these accounts typically pay directly to the named beneficiaries.",
        ],
      },
      {
        heading: "The surprise most families don't expect",
        body: [
          "Beneficiary designations generally override your will. That means an old form — perhaps naming someone from an earlier chapter of life — can control where money goes, even if your will says something different.",
          "It's one of the most common gaps families discover, and one of the easiest to fix.",
        ],
      },
      {
        heading: "A simple habit",
        body: [
          "Review your beneficiaries once a year and after any major life event. Confirm names are spelled correctly, consider naming backup (contingent) beneficiaries, and keep a note of where each account is held.",
        ],
      },
    ],
  },
  {
    slug: "financial-power-of-attorney",
    title: "Financial Power of Attorney Basics",
    category: "Estate Basics",
    minutes: 4,
    excerpt: "Who would pay your bills or manage your accounts if you couldn't? That's exactly what this document answers.",
    takeaways: [
      "A financial power of attorney names someone to handle finances if you're unable to.",
      "Without one, families may need a court process to help — even spouses.",
      "It only matters while you're living; it ends at death.",
    ],
    sections: [
      {
        heading: "What it is",
        body: [
          "A financial power of attorney is a legal document that gives a person you trust — your 'agent' — the ability to manage financial matters on your behalf. That might include paying bills, managing bank accounts, or handling property if you're ever unable to do so yourself.",
        ],
      },
      {
        heading: "Why it matters",
        body: [
          "Many people assume a spouse or adult child could simply step in. In reality, without this document, financial institutions often can't speak with anyone but you — and families may need to go to court to get authority to help.",
          "A little preparation now can spare your family significant stress later.",
        ],
      },
      {
        heading: "Choosing your agent",
        body: [
          "Choose someone trustworthy, organized, and willing to serve. An estate-planning attorney can help you create a document that fits your state's requirements and your family's needs.",
        ],
      },
    ],
  },
  {
    slug: "healthcare-directive-basics",
    title: "Healthcare Directive Basics",
    category: "Family Protection",
    minutes: 4,
    excerpt: "A healthcare directive speaks for you when you can't speak for yourself — and it spares your family from guessing.",
    takeaways: [
      "A healthcare directive records your medical wishes in advance.",
      "A healthcare power of attorney names someone to make decisions with doctors.",
      "Sharing your wishes with family is as important as the document itself.",
    ],
    sections: [
      {
        heading: "What it covers",
        body: [
          "A healthcare directive (sometimes called a living will) describes the medical care you would — or would not — want if you couldn't communicate. A healthcare power of attorney names a person you trust to make medical decisions on your behalf.",
        ],
      },
      {
        heading: "A gift to your family",
        body: [
          "Without clear guidance, loved ones are left to make heavy decisions during emotional moments, sometimes disagreeing about what you would have wanted. Putting your wishes in writing removes that burden. It truly is a gift.",
        ],
      },
      {
        heading: "Getting started",
        body: [
          "Many states offer standard forms, and an attorney can help make sure yours is valid where you live. Once complete, share copies with your chosen decision-maker, your doctor, and a family member — and tell them where the original is kept.",
        ],
      },
    ],
  },
  {
    slug: "organizing-important-documents",
    title: "Organizing Important Documents",
    category: "Getting Organized",
    minutes: 5,
    excerpt: "If your family needed to find your important papers tomorrow, could they? A practical guide to getting organized.",
    takeaways: [
      "Gather wills, policies, account lists, and property documents in one known place.",
      "Tell at least one trusted person where everything is.",
      "A simple checklist beats a perfect system that never gets finished.",
    ],
    sections: [
      {
        heading: "What to gather",
        body: [
          "Start with the essentials: your will or trust, life insurance policies, a list of financial accounts, property deeds, vehicle titles, and any healthcare directives. Add contact information for your attorney, financial professional, and insurance agent if you have them.",
          "You don't need to include account numbers or passwords to make this useful — a simple list of where accounts are held is enough to save your family weeks of searching.",
        ],
      },
      {
        heading: "Where to keep it",
        body: [
          "Choose one secure, findable place — a fireproof home safe or a clearly labeled folder — and resist scattering copies in multiple spots. Some documents, like an original will, should be stored carefully since courts often prefer originals.",
        ],
      },
      {
        heading: "The step most people skip",
        body: [
          "Tell someone. The best-organized folder in the world doesn't help if no one knows it exists. Choose one or two trusted people and let them know where things are and who to call first.",
        ],
      },
    ],
  },
  {
    slug: "common-legacy-questions",
    title: "Common Legacy Planning Questions",
    category: "Getting Organized",
    minutes: 5,
    excerpt: "Honest, plain-English answers to the questions families ask most often when they begin thinking about legacy planning.",
    takeaways: [
      "It's not too early — or too late — to start.",
      "Estate planning is for every family, not just the wealthy.",
      "A short conversation can replace years of uncertainty.",
    ],
    sections: [
      {
        heading: "\"Isn't estate planning just for wealthy people?\"",
        body: [
          "No. If you have a home, a bank account, people you love, or wishes about your care, you have a legacy worth protecting. Planning is about clarity for your family, not the size of your estate.",
        ],
      },
      {
        heading: "\"I'm healthy. Why think about this now?\"",
        body: [
          "Planning works best when it's done calmly, before anyone needs it. Most people describe the feeling afterward as relief — knowing their family won't be left guessing.",
        ],
      },
      {
        heading: "\"Where do I even begin?\"",
        body: [
          "Start with awareness. The Legacy Checkup™ takes about three minutes and shows you which areas of your plan are strong and which may deserve a closer look. From there, a complimentary Legacy Review can help you understand your options — with no pressure and no obligation.",
        ],
      },
    ],
  },
];

export const CATEGORIES = ["All", ...new Set(ARTICLES.map((a) => a.category))];

export const CATEGORY_IMAGES = {
  "Estate Basics": "https://plus.unsplash.com/premium_photo-1661585523076-f48a31a956bf?fm=jpg&q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.1.0",
  "Family Protection": "https://plus.unsplash.com/premium_photo-1683134512986-5d0b728753e2?fm=jpg&q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.1.0",
  "Asset Transfer": "https://images.unsplash.com/photo-1662987619545-1844207dedac?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200&auto=format&fit=crop",
  "Getting Organized": "https://plus.unsplash.com/premium_photo-1664474911801-36c563e1b477?fm=jpg&q=80&w=1200&auto=format&fit=crop&ixlib=rb-4.1.0",
};
